import { formatDeadline } from './offerDeadline';

/**
 * The struck-through "was / now" price for a single package on the pricelist —
 * the PERCENTAGE portion of the offer only.
 *
 * The percentage is linear and per-package (`cart.price * value/100`), so it
 * matches what checkout charges to the cent. A FLAT amount (the `$X credit` in a
 * PERCENTAGE_AND_FLAT_RATE offer) is taken ONCE off the whole order, so it is NOT
 * baked into the per-card price here — folding it into each card would make the
 * cards sum to LESS than checkout charges when the cart holds more than one item.
 * Instead the flat part is surfaced separately (see `orderCredit`) as an
 * order-level "$X credit at checkout" line. So a combined %+flat offer shows a
 * real per-card strike-through (the %) AND the credit, honestly.
 *
 * Returns null only for offers with NO percentage part (pure FLAT_RATE /
 * FREE_SHIPPING / VOLUME_DISCOUNT_FOR_PACKAGE) — those have no honest per-card
 * strike-through; their value still rides the banner + checkout Discount line.
 *
 * Keep this the ONLY place the client computes a discounted price. If you find
 * yourself replicating a coupon calculation elsewhere, call this instead — the
 * one-implementation rule is exactly what kept the two server discount paths from
 * drifting, and it applies on the client too.
 *
 * @param {number|string} price  regular package price
 * @param {object|null} coupon   resolved offer coupon ({ couponType, value, flatValue })
 * @returns {number|null} discounted price (>= 0), or null if there's no percentage part
 */
// Coupon types with a straight-percentage part (in `value`) off the package price.
// The *AND_FLAT_RATE variants ALSO carry a flat order credit in `flatValue` — the
// percentage still shows a clean per-card was/now; the flat rides orderCredit().
const PERCENT_TYPES = [
  'PERCENTAGE',
  'PERCENTAGE_WITH_FREE_SHIPPING',
  'PERCENTAGE_AND_FLAT_RATE',
  'PERCENTAGE_AND_FLAT_RATE_WITH_FREE_SHIPPING',
  // 20% off everything (incl. any 2nd announcement bundle); one announcement
  // bundle is separately free -- see grantsFreeAnnouncements / the add-ons card.
  'PERCENTAGE_WITH_FREE_ANNOUNCEMENTS',
];

// True when the offer makes one announcement bundle free (the announcement cards
// on the add-ons page show it "Free" instead of the %-off price). The server's
// PERCENTAGE_WITH_FREE_ANNOUNCEMENTS branch is the authority on the charge.
export function grantsFreeAnnouncements(coupon) {
  return coupon?.couponType === 'PERCENTAGE_WITH_FREE_ANNOUNCEMENTS';
}

// True when the offer gives 2 free photo magnets with any package (the
// elementary days 15-45 tier, issue #5218). THE ONE client interpreter of that
// tier: the banner, the package-card badge/row/magnet slot, the Buy All card
// ribbon, the magnet-bonus page gate and the "FREE with any package" label all
// ask this. Nothing else may inspect the coupon type for it. The server's
// FREE_MAGNETS_WITH_FREE_SHIPPING branch is the authority on the charge.
export function grantsFreeMagnets(coupon) {
  return coupon?.couponType === 'FREE_MAGNETS_WITH_FREE_SHIPPING';
}

// The magnet tier's own line, e.g. "2 FREE photo magnets with any package +
// free shipping — ends Friday, October 24". No coupon code: the tier is
// auto-applied, and quoting a code would send parents hunting for a box.
export function freeMagnetsLine(coupon) {
  if (!grantsFreeMagnets(coupon)) return null;
  const deadline = formatDeadline(coupon.expiresOn);
  return `2 FREE photo magnets with any package + free shipping${deadline ? ` — ends ${deadline}` : ''}`;
}

// Coupon types that carry a flat dollar credit. For the combined *AND_FLAT_RATE
// types the amount is in `flatValue`; for pure FLAT_RATE it's in `value`.
const FLAT_TYPES = [
  'PERCENTAGE_AND_FLAT_RATE',
  'PERCENTAGE_AND_FLAT_RATE_WITH_FREE_SHIPPING',
  'FLAT_RATE',
  'FLAT_RATE_WITH_FREE_SHIPPING',
];

export default function discountedPrice(price, coupon) {
  if (!coupon || !PERCENT_TYPES.includes(coupon.couponType)) return null;

  const base = Number(price);
  const pct = Number(coupon.value);
  if (Number.isNaN(base) || Number.isNaN(pct) || pct <= 0) return null;

  return Math.max(0, base * (1 - pct / 100));
}

/**
 * The order-level flat credit (a whole-dollar amount taken once off the order),
 * for the "$X credit at checkout" line. Null when the offer has no flat part.
 * @returns {number|null} the credit dollars, or null
 */
export function orderCredit(coupon) {
  if (!coupon || !FLAT_TYPES.includes(coupon.couponType)) return null;

  const isCombined = coupon.couponType.startsWith('PERCENTAGE_AND_FLAT_RATE');
  const amount = Number(isCombined ? coupon.flatValue : coupon.value);
  if (Number.isNaN(amount) || amount <= 0) return null;

  return amount;
}

/**
 * The one-line promo description, e.g. "20% off, $20 credit and free shipping.
 * Use coupon CLASSOF27". Derived from the coupon so the copy always matches the
 * actual discount + code. Returns null if there's nothing quantifiable to state.
 */
export function offerLine(coupon) {
  if (!coupon) return null;
  if (grantsFreeMagnets(coupon)) return freeMagnetsLine(coupon);

  const bits = [];
  if (PERCENT_TYPES.includes(coupon.couponType)) {
    const pct = Number(coupon.value);
    if (!Number.isNaN(pct) && pct > 0) bits.push(`${pct}% off`);
  }
  const credit = orderCredit(coupon);
  if (credit) bits.push(`$${credit} credit`);
  if (grantsFreeAnnouncements(coupon)) bits.push('free announcements');
  if (bits.length === 0) return null;

  if (/FREE_SHIPPING/.test(coupon.couponType)) bits.push('free shipping');

  // "a and b" for two, "a, b and c" for three (Oxford-less, reads naturally).
  const list = bits.length > 1
    ? `${bits.slice(0, -1).join(', ')} and ${bits[bits.length - 1]}`
    : bits[0];
  return `${list}. Use coupon ${coupon.code}`;
}

/**
 * Format a price for a pricelist card — whole dollars read cleaner, cents only when
 * they exist. Shared so senior (PackageChooser) and elementary (PackageDisplay) format
 * the "was / now" identically. Returns null for a non-numeric input.
 */
export function money(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  return n % 1 === 0 ? `$${n}` : `$${n.toFixed(2)}`;
}
